import React, { useEffect, useState } from 'react';
import { z } from 'zod';
import api from 'shared/services/api';
import { toast } from 'react-toastify';
import { useForm, zodResolver } from '@mantine/form';
import { Alert, Button, Group, LoadingOverlay, Modal, Overlay, TextInput } from '@mantine/core';
import { useTranslations } from 'shared/translations/useTranslations';
import EditInspectionActionForm from '../EditInspectionActionForm';
import useFetch from '../../../../../shared/hooks/useFetch';
import { AlertCircle } from 'tabler-icons-react';
import { DeviceType } from '../../../../../shared/types/deviceType';

const schema = z.object({
  name: z.string(),
  description: z.string().optional(),
});

interface Props {
  onClose: () => any;
  placeId: string | number;
  refreshParent: () => any;
  inspectionDeviceId: number;
  inspectionId: number;
}

interface Data {
  deviceType: DeviceType;
}

const CreateInspectionActionModal = (props: Props) => {
  const { refreshParent, onClose, placeId, inspectionDeviceId, inspectionId } = props;
  const translations = useTranslations();
  const [isSaving, setSaving] = useState(false);

  const { data, isLoading, isLoaded, error, refresh } = useFetch<Data>({
    initialValue: {},
    shouldNotFetchAtStart: true,
    fetchAction: () =>
      api({}).get(
        `/api/places/${placeId}/inspections/${inspectionId}/devices/${inspectionDeviceId}`,
        // `/api/places/${placeId}/inspections/${inspectionId}/devices/${inspectionDeviceId}`,
      ),
  });

  const form = useForm({
    initialValues: {
      name: '',
      amount: 1,
      note: '',
    },
    schema: zodResolver(schema),
  });

  const handleClose = () => {
    setOpened(false);
    onClose();
    form.reset();
  };

  const onSubmit = async (formValues: any) => {
    setSaving(true);
    try {
      const body = {
        deviceTypeActionId: formValues.deviceTypeActionId,
        note: formValues.note,
        amount: formValues.amount,
      };
      await api({}).post(
        `/api/places/${placeId}/inspections/${inspectionId}/devices/${inspectionDeviceId}/actions`,
        body,
      );
      toast.success('Akcja została dodana');
      setSaving(false);
      handleClose();
      onClose();
      refreshParent();
    } catch (e) {
      setSaving(false);
      toast.error(translations.global.somethingWentWrong);
    }
  };
  const [opened, setOpened] = useState(false);

  useEffect(() => {
    setOpened(!!inspectionDeviceId);
    if (!inspectionDeviceId) {
      form.reset();
    }
    if (inspectionDeviceId) {
      refresh();
    }
  }, [inspectionDeviceId]);

  return (
    <Modal opened={opened} onClose={handleClose} title={'Dodaj akcję'}>
      <LoadingOverlay visible={isLoading} />
      <form onSubmit={form.onSubmit(onSubmit)}>
        {error && (
          <Alert icon={<AlertCircle size={16} />} title="Bummer!" color="red">
            {translations.global.somethingWentWrong}
          </Alert>
        )}
        {!error && (
          <EditInspectionActionForm
            form={form}
            isCreating
            deviceTypeActions={
              data?.deviceType?.deviceTypeActions?.map(dta => ({
                label: dta.name,
                value: dta.id,
              })) || []
            }
          />
        )}
        <Alert color="yellow" title="Uwaga" style={{ marginTop: 20 }}>
          Dodając nową akcję <strong>należy pamiętać aby </strong>
          później manualnie edytować kolejne lub poprzednie akcję czy szkodniki. Inaczej wartości
          mogą się nie zgadzać. Tyczy się to również zmian wymaganych w innych przeglądach.
          <br />
          <br />
          Podczas dodania akcji , <strong>data</strong> jej jest ustawiona na datę stworzenia
          przeglądu. Później można ją edytować.

          <br /><br />
          <strong>Uwaga:</strong> Akcje czyszczenia lepa należy dodawać przed dodaniem szkodnika w danym przeglądzie, inaczej wpłynie to na poprawne wyliczenia statystyki
        </Alert>
        <Group position="right" mt={20}>
          <Button color="primary" loading={isSaving} type="submit">
            {translations.global.add}
          </Button>
          <Button variant="outline" onClick={handleClose}>
            {translations.global.cancel}
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default CreateInspectionActionModal;
